import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { EventqToastService } from '@ecam/eventq-components';
import { createAlarmsClipModeReset, PendingAction } from '../../../core/models/browser-tab.models';
import {
  loadingActionFromRoutePath,
  tabProcessingRouteSegments,
} from '../../../core/routing/browser-tab-routes';
import { AgentPreferencesService } from '../../../core/services/agent-preferences.service';
import { AgentDataService } from '../../../core/services/agent-data.service';
import { AlarmViewedStorageService } from '../../../core/services/alarm-viewed-storage.service';
import { clearTalkdownAttempts } from '../../../core/services/talkdown-attempts-storage';
import { AuthSessionService } from '../../../core/services/auth-session.service';
import { BrowserTabNavigationService } from '../../../core/services/browser-tab-navigation.service';
import { BrowserTabService } from '../../../core/services/browser-tab.service';
import { EventHandleService } from '../../../core/services/event-handle.service';
import { formatParkDurationLabel } from '../../../core/utils/park-duration.util';
import { parseDealerSiteId } from '../../../core/utils/dealer-event-id.util';
import { AgentEventsService } from '../../../core/services/agent-events.service';
import { ProcessingTimerService } from '../../../core/services/processing-timer.service';

/** Start-patrol / start-site-check was rejected because the site already has work. */
export interface PatrolStartConflict {
  kind: 'queue' | 'in-use';
  eventId: string;
}

/**
 * Completes action loading flows (patrol, site check, process event, raise/close event).
 * Mock mode uses configurable delay from events mock settings.
 */
@Injectable({ providedIn: 'root' })
export class ActionLoadingService {
  private readonly agentData = inject(AgentDataService);
  private readonly browserTabs = inject(BrowserTabService);
  private readonly tabNavigation = inject(BrowserTabNavigationService);
  private readonly preferences = inject(AgentPreferencesService);
  private readonly toast = inject(EventqToastService);
  private readonly eventHandles = inject(EventHandleService);
  private readonly auth = inject(AuthSessionService);
  private readonly agentEvents = inject(AgentEventsService);
  private readonly processingTimers = inject(ProcessingTimerService);
  private readonly viewedAlarms = inject(AlarmViewedStorageService);
  /** Blocks a second patrol or site-check start while one request or its dialog is open. */
  private patrolStartLocked = false;
  /** Bumped when the loading page leaves so an in-flight start cannot reopen the dialog. */
  private patrolStartEpoch = 0;
  readonly startConflict = signal<PatrolStartConflict | null>(null);

  async runLoadingFlow(pending: PendingAction): Promise<void> {
    const isPatrolStart =
      (pending.type === 'manual-patrol' || pending.type === 'site-check') && Boolean(pending.siteId);
    let patrolStartEpoch = 0;
    if (isPatrolStart) {
      if (this.patrolStartLocked) {
        return;
      }
      this.patrolStartLocked = true;
      patrolStartEpoch = ++this.patrolStartEpoch;
    }

    try {
      if (pending.type === 'process-event' && pending.eventId) {
        await this.tabNavigation.openClaimedProcessEvent(pending.eventId);
        return;
      }

      const settings = await firstValueFrom(this.agentData.getEventsMockSettings());
      await this.delay(settings.loadingDelayMs);

      if (pending.type === 'raise-to-resolve' && pending.eventId && pending.outcomeId) {
        const response = await firstValueFrom(
          this.agentData.raiseEventToResolve(pending.eventId, { outcomeId: pending.outcomeId }),
        );
        const resolvedEventId = response.eventId?.trim() || pending.eventId;
        this.toast.raisedToResolve(
          pending.eventLocation ?? resolvedEventId,
          pending.outcomeLabel ?? pending.outcomeId,
        );
        this.viewedAlarms.clearEvent(pending.eventId);
        await this.completeEventAction(resolvedEventId, 'raise-to-resolve');
        return;
      }

      if (pending.type === 'close-event' && pending.eventId && pending.outcomeId) {
        await firstValueFrom(
          this.agentData.closeEvent(pending.eventId, {
            outcomeId: pending.outcomeId,
            closingDetails: pending.closingDetails ?? '',
          }),
        );
        const closedLabel = pending.eventLocation ?? pending.eventId;
        this.toast.eventClosed(closedLabel);
        this.agentEvents.broadcastEventClosed(pending.eventId, closedLabel);
        await this.completeEventAction(pending.eventId, 'close-event');
        return;
      }

      if (pending.type === 'park-event' && pending.eventId) {
        const durationMinutes = pending.durationMinutes ?? 0;
        const [detail, queue] = await Promise.all([
          firstValueFrom(this.agentData.getEventDetail(pending.eventId)),
          firstValueFrom(this.agentData.getEventsQueue()),
        ]);
        const queueRow = queue.items.find((item) => item.id === pending.eventId);
        const parkedBy = this.auth.displayName() ?? this.auth.email()?.split('@')[0] ?? 'Agent';
        await firstValueFrom(
          this.agentData.parkEvent(pending.eventId, {
            durationMinutes,
            location: pending.eventLocation ?? detail.location,
            patrolStarted: queueRow?.firstAlarmDetected ?? detail.currentTime ?? '',
            parkedBy,
          }),
        );
        const parkedLabel = pending.eventLocation ?? detail.location;
        const durationLabel = formatParkDurationLabel(durationMinutes);
        this.toast.eventParked(parkedLabel, durationLabel);
        this.agentEvents.broadcastEventParked(pending.eventId, parkedLabel, durationLabel);
        await this.completeEventAction(pending.eventId, 'park-event');
        return;
      }

      if ((pending.type === 'manual-patrol' || pending.type === 'site-check') && pending.siteId) {
        let locationName = pending.eventLocation;
        if (!locationName) {
          const locations = await firstValueFrom(this.agentData.getSiteLocations());
          locationName =
            locations.items.find((site) => site.id === pending.siteId)?.name ?? pending.siteId;
        }

        try {
          const request = { siteId: pending.siteId, siteName: locationName };
          const response =
            pending.type === 'manual-patrol'
              ? await firstValueFrom(this.agentData.startManualPatrol(request))
            : await firstValueFrom(this.agentData.startSiteCheck(request));

        if (patrolStartEpoch !== this.patrolStartEpoch) {
          return;
        }

        this.browserTabs.updateActivePageState({
            eventProcessingMode: pending.type,
            activeEventId: response.eventId,
            activeEventSiteId: parseDealerSiteId(pending.siteId),
            camerasPlaybackAnchorAt: null,
            pendingAction: null,
            claimedAt: response.startedAt,
          });
          this.browserTabs.updateTabLabel(
            this.browserTabs.activeTabId(),
            response.location || locationName,
          );
          await this.tabNavigation.navigateWithinActiveTab(
            ...tabProcessingRouteSegments(pending.type, response.eventId),
          );
          this.processingTimers.startForTab(this.browserTabs.activeTabId(), {
            entityId: response.eventId,
            entityType: pending.type,
            claimedAt: response.startedAt,
          });
          this.agentEvents.broadcastStateSync();
        } catch (error: unknown) {
        if (isPatrolStart && patrolStartEpoch !== this.patrolStartEpoch) {
          return;
        }

        const conflict = readPatrolStartConflict(error);
        if (conflict) {
          this.startConflict.set(conflict);
          return;
        }

          if (error instanceof HttpErrorResponse && error.status === 409) {
            await this.leaveLoadingForDashboard();
            return;
          }

          const detail =
            error instanceof HttpErrorResponse &&
            error.error &&
            typeof error.error === 'object' &&
            typeof (error.error as { detail?: unknown }).detail === 'string'
              ? (error.error as { detail: string }).detail
              : null;
          this.browserTabs.updateActivePageState({ pendingAction: null });
          this.toast.show('notice', {
            title: detail?.includes('Open work limit')
              ? 'Open work limit reached'
              : 'Unable to start',
            subtitle: detail ?? 'The request failed. Try again or close other work first.',
            tone: 'error',
          });
          await this.tabNavigation.navigateWithinActiveTab();
        }
        return;
      }

      this.browserTabs.updateActivePageState({ pendingAction: null });
      await this.tabNavigation.navigateWithinActiveTab();
    } finally {
      if (
        isPatrolStart &&
        patrolStartEpoch === this.patrolStartEpoch &&
        this.startConflict() == null
      ) {
        this.patrolStartLocked = false;
      }
    }
  }

  /** Drops a start conflict without navigating. Used when the loading page is left. */
  releaseStartConflict(): void {
    this.patrolStartEpoch++;
    this.startConflict.set(null);
    this.patrolStartLocked = false;
  }

  /** Closes a start conflict and returns to the events dashboard without claiming or starting. */
  async dismissStartConflict(): Promise<void> {
    if (!this.startConflict()) {
      return;
    }
    this.startConflict.set(null);
    this.patrolStartLocked = false;
    await this.leaveLoadingForDashboard();
  }

  /** Claims the queued event from an EVENT_IN_QUEUE conflict and opens it for processing. */
  async confirmProcessQueuedEvent(): Promise<void> {
    const conflict = this.startConflict();
    if (!conflict || conflict.kind !== 'queue') {
      return;
    }

    this.startConflict.set(null);
    const eventId = conflict.eventId;
    const tabId = this.browserTabs.activeTabId();
    let claimed: Awaited<ReturnType<EventHandleService['claimEventForTab']>> = null;
    try {
      claimed = await this.eventHandles.claimEventForTab(tabId, eventId);
    } catch {
      claimed = null;
    } finally {
      this.patrolStartLocked = false;
    }

    if (!claimed?.ok) {
      const workLimit = claimed != null && claimed.conflict.code === 'WORK_LIMIT';
      this.toast.show('notice', {
        title: workLimit ? 'Open work limit reached' : 'Unable to open event',
        subtitle:
          claimed && !claimed.ok
            ? claimed.conflict.message
            : 'This event is unavailable or already held by another agent.',
        tone: 'error',
      });
      await this.leaveLoadingForDashboard();
      return;
    }

    await this.tabNavigation.openClaimedProcessEvent(claimed.handle.eventId);
  }

  /** Recovers navigation when reload lands on a loading route without a pending action. */
  async recoverInterruptedLoad(routePath: string): Promise<void> {
    const action = loadingActionFromRoutePath(routePath);
    const state = this.browserTabs.activePageState();

    if (
      action === 'raise-to-resolve' ||
      action === 'close-event' ||
      action === 'park-event' ||
      action === 'process-event'
    ) {
      if (state.activeEventId) {
        if (action !== 'process-event' && this.preferences.autoProcess()) {
          const reason =
            action === 'park-event'
              ? 'park-event'
              : action === 'raise-to-resolve'
                ? 'raise-to-resolve'
                : 'close-event';
          await this.completeEventAction(state.activeEventId, reason);
          return;
        }

        await this.tabNavigation.navigateWithinActiveTab(
          ...tabProcessingRouteSegments(state.eventProcessingMode, state.activeEventId),
        );
        this.processingTimers.startForTab(this.browserTabs.activeTabId(), {
          entityId: state.activeEventId,
          entityType: state.eventProcessingMode,
          claimedAt: state.claimedAt ?? undefined,
        });
        return;
      }
    }

    await this.tabNavigation.navigateWithinActiveTab();
  }

  private async completeEventAction(
    currentEventId: string,
    reason: 'close-event' | 'park-event' | 'raise-to-resolve',
  ): Promise<void> {
    const finishedTabId =
      this.browserTabs.findTab(currentEventId)?.id ?? this.browserTabs.activeTabId();
    this.processingTimers.completeForTab(finishedTabId, reason);
    await this.releaseFinishedTabHandle(finishedTabId);
    this.viewedAlarms.clearEvent(currentEventId);
    if (reason === 'close-event' || reason === 'park-event') {
      clearTalkdownAttempts(currentEventId);
    }
    if (!this.browserTabs.hasTab(finishedTabId)) {
      return;
    }

    this.browserTabs.updatePageState(finishedTabId, { pendingAction: null });
    this.browserTabs.updateTabLabel(finishedTabId, 'New Event');
    this.browserTabs.updateTabRoutePath(finishedTabId, 'events');

    const tabs = this.browserTabs.tabs();
    const otherTabs = tabs.filter((tab) => tab.id !== finishedTabId);
    const otherEmptyChrome = otherTabs.some(
      (tab) => !this.browserTabs.getPageState(tab.id).activeEventId,
    );

    // Drop the finished workspace when another tab remains and auto-process is
    // on, or when a New Event / empty chrome tab already exists (avoid duplicates).
    if (otherTabs.length > 0 && (this.preferences.autoProcess() || otherEmptyChrome)) {
      const index = tabs.findIndex((tab) => tab.id === finishedTabId);
      const fallbackId = tabs[index + 1]?.id ?? tabs[index - 1]?.id;
      const stayOnId =
        this.browserTabs.activeTabId() !== finishedTabId
          ? this.browserTabs.activeTabId()
          : fallbackId;
      this.browserTabs.removeTab(finishedTabId);
      const nextId =
        stayOnId && this.browserTabs.hasTab(stayOnId)
          ? stayOnId
          : this.browserTabs.activeTabId();
      await this.tabNavigation.navigateToTab(nextId);
      return;
    }

    // Auto-process off: return to the events landing page on this tab.
    if (!this.preferences.autoProcess()) {
      this.browserTabs.selectTab(finishedTabId);
      await this.tabNavigation.navigateWithinActiveTab();
      return;
    }

    // Auto-process on, sole tab: claim the next queue event into this workspace.
    const claimed = await this.eventHandles.claimNextForTab(finishedTabId, {
      excludeEventIds: [currentEventId],
    });
    if (claimed?.ok) {
      this.browserTabs.selectTab(finishedTabId);
      await this.tabNavigation.openClaimedProcessEvent(claimed.handle.eventId);
      return;
    }

    this.browserTabs.selectTab(finishedTabId);
    await this.tabNavigation.navigateWithinActiveTab();
  }

  private async releaseFinishedTabHandle(tabId: string): Promise<void> {
    if (this.browserTabs.activeTabId() === tabId) {
      await this.eventHandles.releaseActiveTabHandle();
      return;
    }

    const state = this.browserTabs.getPageState(tabId);
    await this.eventHandles.releaseHandleForTabState(state.eventHandleId);
    this.browserTabs.updatePageState(tabId, {
      activeEventId: null,
      activeEventSiteId: null,
      eventHandleId: null,
      claimedAt: null,
      pendingAction: null,
      ...createAlarmsClipModeReset(),
    });
  }

  private async leaveLoadingForDashboard(): Promise<void> {
    this.browserTabs.updateActivePageState({
      pendingAction: null,
      eventProcessingMode: 'process-event',
      activeEventId: null,
      activeEventSiteId: null,
      claimedAt: null,
    });
    this.browserTabs.updateTabLabel(this.browserTabs.activeTabId(), 'New Event');
    await this.tabNavigation.navigateWithinActiveTab();
  }

  private delay(ms: number): Promise<void> {
    if (ms <= 0) {
      return Promise.resolve();
    }
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

const PATROL_START_IN_USE_CODES = new Set(['EVENT_CLAIMED', 'EVENT_ESCALATED', 'EVENT_PARKED']);

function readPatrolStartConflict(error: unknown): PatrolStartConflict | null {
  if (!(error instanceof HttpErrorResponse) || error.status !== 409) {
    return null;
  }

  const body = error.error;
  if (!body || typeof body !== 'object') {
    return null;
  }

  const code = (body as { code?: unknown }).code;
  const eventId = (body as { details?: { eventId?: unknown } }).details?.eventId;
  if (typeof eventId !== 'string' || eventId.trim() === '') {
    return null;
  }

  if (code === 'EVENT_IN_QUEUE') {
    return { kind: 'queue', eventId };
  }

  if (typeof code === 'string' && PATROL_START_IN_USE_CODES.has(code)) {
    return { kind: 'in-use', eventId };
  }

  return null;
}
